import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { BiCreditCard } from 'react-icons/bi'
import { FaCcVisa, FaCcMastercard } from 'react-icons/fa'
// Removido ícone inexistente de 'react-icons/si'
import { MdKeyboardArrowDown, MdOutlineAddCard } from 'react-icons/md'
import CardAddForm, { CardFormData, toCreateCardPayload } from './components/CardAddForm'
import { HiOutlineCreditCard } from 'react-icons/hi'
import { usePrepareCart } from '../../../../../hooks/usePrepareCart'
import { WarningBanners } from '../resumeStep/WarningBanners'
import { listSavedCards, createCard, findCreatedCardExternalId } from '../../../../../services/cards'
import api from '../../../../../services/api'
import { calculateShippingCost } from '../../../../../utils/constants'

interface TwoCardsPaymentProps {
  selectedMethod: string
  cartItems: any[]
  shippingAddress?: any
}

export const TwoCardsPayment: React.FC<TwoCardsPaymentProps> = ({
  selectedMethod: _selectedMethod,
  cartItems: _cartItems,
  shippingAddress
}) => {
  const [percentage1, setPercentage1] = useState(50)
  const [percentage2, setPercentage2] = useState(50)

  type SavedCardUI = {
    id: number
    external_id: string
    brand: string
    type: string
    last4: string
    holder: string
  }
  const [cards, setCards] = useState<SavedCardUI[]>([])
  const [loadingCards, setLoadingCards] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [selectedCardExternalId1, setSelectedCardExternalId1] = useState<string>('')
  const [selectedCardExternalId2, setSelectedCardExternalId2] = useState<string>('')
  const [cvv1, setCvv1] = useState('')
  const [cvv2, setCvv2] = useState('')
  const [temporaryCvv, setTemporaryCvv] = useState<{ [cardId: string]: string }>({})
  const [installments1, setInstallments1] = useState<number | null>(null)
  const [installments2, setInstallments2] = useState<number | null>(null)
  const [installmentsList, setInstallmentsList] = useState<
    Array<{ id: number; installment: string; amount: number; fees: string; total: number }>
  >([])
  const [loadingInstallments, setLoadingInstallments] = useState(false)

  const [showForm1, setShowForm1] = useState(false)
  const [showForm2, setShowForm2] = useState(false)

  const { lastOrderCreated } = usePrepareCart()
  const form = useFormContext<{ cvv1: string; cvv2: string }>()

  const reloadCards = async () => {
    try {
      setLoadingCards(true)
      setError(null)
      const fetched = await listSavedCards()
      const mapped: SavedCardUI[] = fetched.map((c: any) => ({
        id: c.id,
        external_id: c.external_id || String(c.id),
        brand: c.brand || c.type || 'Cartão',
        type: c.brand || c.type || 'Cartão',
        last4: c.last4 || c.last_digits || '0000',
        holder: c.holder_name || c.holder || ''
      }))
      setCards(mapped)

      if (!selectedCardExternalId1 && mapped[0]?.external_id) {
        setSelectedCardExternalId1(mapped[0].external_id)
      }
      if (!selectedCardExternalId2 && mapped[1]?.external_id) {
        setSelectedCardExternalId2(mapped[1].external_id)
      }
      if (mapped.length === 0) {
        setShowForm1(true)
        setShowForm2(true)
      } else if (mapped.length === 1) {
        // Com apenas 1 cartão salvo, mantém o Cartão 1 selecionado e abre o formulário do Cartão 2 automaticamente
        setShowForm2(true)
      }
    } catch (e) {
      setError('Não foi possível carregar cartões salvos')
    } finally {
      setLoadingCards(false)
    }
  }
  // Evita que o mesmo cartão seja selecionado nos dois selects
  const cardsForFirst = useMemo(
    () => cards.filter((c) => c.external_id !== selectedCardExternalId2),
    [cards, selectedCardExternalId2]
  )
  const cardsForSecond = useMemo(
    () => cards.filter((c) => c.external_id !== selectedCardExternalId1),
    [cards, selectedCardExternalId1]
  )
  // Garante IDs distintos caso o usuário selecione o mesmo nos dois
  useEffect(() => {
    if (selectedCardExternalId1 && selectedCardExternalId1 === selectedCardExternalId2) {
      const alternative = cards.find((c) => c.external_id !== selectedCardExternalId1)?.external_id || ''
      setSelectedCardExternalId2(alternative)
    }
  }, [selectedCardExternalId1, selectedCardExternalId2, cards])

  const handleAddCard = async (data: CardFormData, target: 'first' | 'second') => {
    try {
      const created = await createCard(toCreateCardPayload(data))
      const fetched = await listSavedCards()
      const mapped: SavedCardUI[] = fetched.map((c: any) => ({
        id: c.id,
        external_id: c.external_id || String(c.id),
        brand: c.brand || c.type || 'Cartão',
        type: c.brand || c.type || 'Cartão',
        last4: c.last4 || c.last_digits || '0000',
        holder: c.holder_name || c.holder || ''
      }))
      setCards(mapped)

      const createdExternalId = findCreatedCardExternalId(created, fetched, data.number)

      // Armazena temporariamente o CVV do cartão recém-criado
      if (createdExternalId && data.cvv) {
        setTemporaryCvv((prev) => ({
          ...prev,
          [createdExternalId]: data.cvv
        }))
      }

      if (target === 'first') {
        if (createdExternalId) {
          setSelectedCardExternalId1(createdExternalId)
          // Preenche automaticamente o CVV do primeiro cartão
          setCvv1(data.cvv)
          form?.setValue?.('cvv1', data.cvv, { shouldValidate: true })
        }
      } else if (createdExternalId) {
        setSelectedCardExternalId2(createdExternalId)
        // Preenche automaticamente o CVV do segundo cartão
        setCvv2(data.cvv)
        form?.setValue?.('cvv2', data.cvv, { shouldValidate: true })
      }
    } catch (e) {
      setError('Não foi possível adicionar o cartão')
    }
  }

  const handleAddCard1 = async (data: CardFormData) => {
    await handleAddCard(data, 'first')
    setShowForm1(false)
  }

  const handleAddCard2 = async (data: CardFormData) => {
    await handleAddCard(data, 'second')
    setShowForm2(false)
  }

  const getCardIcon = (brand: string) => {
    switch (brand) {
      case 'Visa':
        return <FaCcVisa className="text-xl text-blue-600" />
      case 'Mastercard':
        return <FaCcMastercard className="text-xl text-red-600" />
      case 'Elo':
        return <BiCreditCard className="text-xl text-green-600" />
      default:
        return <BiCreditCard className="text-xl text-gray-500" />
    }
  }

  // Calcula o total usando a mesma lógica do resumo para manter consistência visual
  const validItems = useMemo(() => (_cartItems || []).filter((it: any) => (it?.quantity || 0) > 0), [_cartItems])
  const subtotal = useMemo(
    () => validItems.reduce((acc: number, item: any) => acc + parseFloat(item.amount || '0'), 0),
    [validItems]
  )
  const productDiscounts = useMemo(() => {
    return validItems.reduce((acc: number, item: any) => {
      const itemDiscount = parseFloat(item.discount || '0')
      if (itemDiscount === 0 && item.product?.price && item.product?.real_price) {
        const originalPrice = item.product.price * (item.quantity || 0)
        const finalPrice = item.product.real_price * (item.quantity || 0)
        const calculatedDiscount = Math.max(0, originalPrice - finalPrice)
        return acc + calculatedDiscount
      }
      return acc + itemDiscount
    }, 0)
  }, [validItems])
  const freeShippingThreshold = 2300
  const importCostShipping = useMemo(() => {
    const orderItems = lastOrderCreated?.items
    const source = orderItems?.length ? orderItems : validItems
    return calculateShippingCost(source as any[])
  }, [lastOrderCreated?.items, validItems])
  const remainingForFreeShipping = useMemo(
    () => Math.max(0, freeShippingThreshold - (subtotal + importCostShipping - productDiscounts)),
    [subtotal, importCostShipping, productDiscounts]
  )
  const importDiscount = useMemo(
    () => (remainingForFreeShipping <= 0 ? importCostShipping : 0),
    [remainingForFreeShipping, importCostShipping]
  )
  const computedTotal = useMemo(
    () => subtotal + importCostShipping - productDiscounts - importDiscount,
    [subtotal, importCostShipping, productDiscounts, importDiscount]
  )
  const totalAmount = useMemo(() => {
    const orderAmt = Number(lastOrderCreated?.amount)
    if (Number.isFinite(orderAmt) && orderAmt > 0) return orderAmt
    return computedTotal
  }, [lastOrderCreated?.amount, computedTotal])
  const amountCard1 = useMemo(() => (totalAmount * percentage1) / 100, [totalAmount, percentage1])
  const amountCard2 = useMemo(() => Math.max(0, totalAmount - amountCard1), [totalAmount, amountCard1])
  const formatBRL = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

  useEffect(() => {
    reloadCards()

    // Limpa CVVs temporários quando o componente é desmontado
    return () => {
      setTemporaryCvv({})
    }
  }, [])

  useEffect(() => {
    const fetchInstallments = async () => {
      try {
        setLoadingInstallments(true)
        const { data } = await api.get(`/payments/installments/${lastOrderCreated?.id}`)
        const list = Array.isArray(data?.installments) ? data.installments : []
        setInstallmentsList(list)
        // Limpa a seleção anterior para forçar o usuário a selecionar novamente
        localStorage.removeItem('@CANNECT:TWO_CARDS_SELECTION')
      } catch {
        setInstallmentsList([])
      } finally {
        setLoadingInstallments(false)
      }
    }
    if (lastOrderCreated?.id) fetchInstallments()
  }, [lastOrderCreated?.id])

  const selectedInstallment1 = useMemo(
    () => installmentsList.find((i) => i.id === installments1),
    [installmentsList, installments1]
  )
  const selectedInstallment2 = useMemo(
    () => installmentsList.find((i) => i.id === installments2),
    [installmentsList, installments2]
  )
  const feesFactor1 = useMemo(() => {
    if (!selectedInstallment1 || totalAmount <= 0) return 1
    const total = Number(selectedInstallment1.total)
    return total > 0 ? total / Number(totalAmount) : 1
  }, [selectedInstallment1, totalAmount])
  const feesFactor2 = useMemo(() => {
    if (!selectedInstallment2 || totalAmount <= 0) return 1
    const total = Number(selectedInstallment2.total)
    return total > 0 ? total / Number(totalAmount) : 1
  }, [selectedInstallment2, totalAmount])

  useEffect(() => {
    try {
      const payload = {
        selectedCardExternalId1,
        selectedCardExternalId2,
        installments1,
        installments2,
        percentage1,
        percentage2,
        amountCard1,
        amountCard2,
        feesFactor1,
        feesFactor2
      }
      localStorage.setItem('@CANNECT:TWO_CARDS_SELECTION', JSON.stringify(payload))
    } catch {
      // ignore
    }
  }, [
    selectedCardExternalId1,
    selectedCardExternalId2,
    installments1,
    installments2,
    percentage1,
    percentage2,
    amountCard1,
    amountCard2,
    feesFactor1,
    feesFactor2
  ])

  // Validação apenas visual (não bloqueia o resumo)
  // const canSubmit = !!lastOrderCreated?.id && amountCard1 > 0 && amountCard2 > 0 && percentage1 + percentage2 === 100

  return (
    <div className="h-fit w-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-8">
      <div className="space-y-4 sm:space-y-6">
        {/* Cabeçalho */}
        <div>
          <div className="mb-2 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 text-primary-700">
                <HiOutlineCreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-xs sm:text-sm">+</span>
                <HiOutlineCreditCard className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <h2 className="text-base font-semibold text-primary-700 sm:text-lg">Pagamento com 2 cartões</h2>
            </div>
            <p className="text-xs text-neutral-800 sm:text-sm">Selecione um cartão para efetuar o pagamento.</p>
          </div>
        </div>

        {/* Cartão 1 */}
        <div className="space-y-4 rounded-lg border border-gray-100 bg-gray-50 p-4 sm:space-y-6 sm:p-6">
          <h3 className="flex items-center gap-2 text-base font-semibold text-neutral-800">
            <BiCreditCard className="text-lg" />
            Cartão 1
          </h3>
          <hr
            style={{ borderTop: '0.2px solid #E3E3E3', borderLeft: 'none', borderRight: 'none', borderBottom: 'none' }}
          />

          {/* Porcentagem */}
          <div>
            <span className="mb-2 block text-sm text-neutral-800">Porcentagem do cartão</span>
            <div className="flex items-center gap-1 sm:gap-4">
              {[10, 25, 50, 75, 90].map((val) => (
                <button
                  key={val}
                  onClick={() => {
                    setPercentage1(val)
                    setPercentage2(100 - val)
                  }}
                  style={{
                    border: '1px solid #A2B156'
                  }}
                  className={`inline-flex h-6 w-10 flex-shrink-0 items-center justify-center rounded-xl px-1.5 py-0.5 text-xs font-medium sm:h-6 sm:w-15 sm:rounded-3xl sm:px-4 sm:text-base ${
                    percentage1 === val ? 'bg-[#A2B156] text-white' : 'bg-white text-[#A2B156] hover:bg-lime-100'
                  }`}>
                  {val}%
                </button>
              ))}
              <div
                style={{ border: '1px solid var(--Neutrals-Neutrals200, #CCCBCB)' }}
                className="inline-flex h-6 w-16 flex-shrink-0 items-center justify-center rounded-xl bg-[var(--Secondary-Secondary100,#EDECE7)] px-2 py-0.5 text-xs font-medium text-neutral-500 sm:h-6 sm:w-23 sm:rounded-3xl sm:px-4 sm:py-[6px] sm:text-base">
                {formatBRL(amountCard1 * feesFactor1)}
              </div>
            </div>
          </div>

          {/* Cartão e CVV */}
          <div className="space-y-4 sm:flex sm:flex-wrap sm:items-center sm:gap-4 sm:space-y-0">
            <div className="flex items-center gap-1 text-sm font-semibold text-neutral-800 sm:flex-none">
              <BiCreditCard className="h-4 w-4 text-neutral-800" />
              Cartão:
            </div>

            <div className="relative flex-1 sm:flex-1">
              <select
                style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}
                className="h-9 w-full appearance-none rounded-md bg-white py-1 pl-10 pr-10 text-xs text-gray-700 shadow-sm focus:outline-none sm:h-11 sm:py-2 sm:pl-10 sm:pr-12 sm:text-xs"
                value={selectedCardExternalId1}
                onChange={(e) => {
                  setSelectedCardExternalId1(e.target.value)
                  // Preenche CVV automaticamente se disponível
                  const cvv = temporaryCvv[e.target.value]
                  if (cvv) {
                    setCvv1(cvv)
                    form?.setValue?.('cvv1', cvv, { shouldValidate: true })
                  } else {
                    setCvv1('')
                    form?.setValue?.('cvv1', '', { shouldValidate: true })
                  }
                }}>
                {cardsForFirst.map((card: SavedCardUI) => (
                  <option key={card.id} value={card.external_id} className="py-2 text-base sm:py-1 sm:text-xs">
                    {`**** ${card.last4}`}
                  </option>
                ))}
              </select>
              {/* Ícone à esquerda não deve bloquear cliques no select */}
              <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                {getCardIcon(cards.find((c: SavedCardUI) => c.external_id === selectedCardExternalId1)?.brand || '')}
              </div>
              <MdKeyboardArrowDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 sm:right-3 sm:h-6 sm:w-5" />
            </div>

            <div className="flex items-center gap-2 sm:flex-none">
              <span className="text-sm text-neutral-500">
                CVV<span className="text-red-500">*</span>
              </span>
              <input
                id="cvv-input"
                aria-label="CVV"
                style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}
                className="h-11 w-16 rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-700 outline-none sm:w-18"
                placeholder="123"
                {...(form?.register ? form.register('cvv1', { required: true, pattern: /^\d{3,4}$/u }) : {})}
                maxLength={4}
                value={cvv1}
                onChange={(e) => {
                  setCvv1(e.target.value)
                  form?.setValue?.('cvv1', e.target.value, { shouldValidate: true })
                }}
              />
              <button
                onClick={() => setShowForm1(true)}
                className="flex items-center border-0 bg-transparent text-xs font-medium text-primary-800 hover:underline">
                <MdOutlineAddCard className="mr-1 h-4 w-4 text-primary-800" />
                <span className="hidden sm:inline">Adicionar</span>
                <span className="sm:hidden">+</span>
              </button>
            </div>
          </div>

          {/* Parcelas */}
          <div className="space-y-2 sm:flex sm:items-center sm:gap-7 sm:space-y-0">
            <span className="block text-sm text-gray-700 sm:whitespace-nowrap">Parcelas</span>
            <div className="relative flex-1">
              <select
                id="installments-select"
                aria-label="Parcelas"
                style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}
                className="h-9 w-full appearance-none rounded-md bg-white px-4 pr-8 text-sm text-gray-700 shadow-sm focus:outline-none"
                value={installments1 || ''}
                disabled={loadingInstallments}
                onChange={(e) => {
                  const { value } = e.target
                  if (value === '') {
                    setInstallments1(null)
                    return
                  }
                  setInstallments1(Number(value))
                }}>
                <option value="">Selecione a quantidade de parcelas</option>
                {installmentsList.map((it) => {
                  const fraction = totalAmount > 0 ? amountCard1 / totalAmount : 0
                  const perInstallment = Number(it.amount) * fraction
                  const perTotal = Number(it.total) * fraction
                  const label = `${it.installment} de ${formatBRL(perInstallment)} ${
                    it.fees === '(sem juros)' ? '- sem juros' : `(total ${formatBRL(perTotal)})`
                  }`
                  return (
                    <option key={it.id} value={it.id}>
                      {label}
                    </option>
                  )
                })}
              </select>
              <MdKeyboardArrowDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 sm:right-3 sm:h-6 sm:w-5" />
            </div>
          </div>
          <div>
            <hr
              style={{
                borderTop: '0.3px solid #E3E3E3',
                borderLeft: 'none',
                borderRight: 'none',
                borderBottom: 'none'
              }}
            />
          </div>

          {/* Valor a pagar - Cartão 1 */}
          <div className="flex items-center justify-between border-t border-gray-200">
            <span className="text-xs text-neutral-800 sm:text-sm">Valor a pagar Cartão 1:</span>
            <span className="text-lg font-semibold text-neutral-800 sm:text-2xl">
              {formatBRL(amountCard1 * feesFactor1)}
            </span>
          </div>
        </div>

        {showForm1 && (
          <CardAddForm
            onSubmit={handleAddCard1}
            onCancel={() => setShowForm1(false)}
            shippingAddress={shippingAddress}
          />
        )}

        {/* Cartão 2 */}
        <div className="space-y-4 rounded-lg border border-gray-100 bg-gray-50 p-4 sm:space-y-6 sm:p-6">
          <h3 className="flex items-center gap-2 text-base font-semibold text-neutral-800">
            <BiCreditCard className="text-lg" />
            Cartão 2
          </h3>
          <hr
            style={{ borderTop: '0.2px solid #E3E3E3', borderLeft: 'none', borderRight: 'none', borderBottom: 'none' }}
          />

          {/* Porcentagem Cartão 2 */}
          <div>
            <span className="mb-2 block text-sm text-neutral-800">Porcentagem do cartão</span>
            <div className="flex items-center gap-1 sm:gap-4">
              {[10, 25, 50, 75, 90].map((val) => (
                <button
                  key={val}
                  onClick={() => {
                    setPercentage2(val)
                    setPercentage1(100 - val)
                  }}
                  style={{ border: '1px solid #A2B156' }}
                  className={`inline-flex h-6 w-10 flex-shrink-0 items-center justify-center rounded-xl px-1.5 py-0.5 text-xs font-medium sm:h-6 sm:w-15 sm:rounded-3xl sm:px-4 sm:text-base ${
                    percentage2 === val ? 'bg-[#A2B156] text-white' : 'bg-white text-[#A2B156] hover:bg-lime-100'
                  }`}>
                  {val}%
                </button>
              ))}
              <div
                style={{ border: '1px solid var(--Neutrals-Neutrals200, #CCCBCB)' }}
                className="inline-flex h-6 w-16 flex-shrink-0 items-center justify-center rounded-xl bg-[var(--Secondary-Secondary100,#EDECE7)] px-2 py-0.5 text-xs font-medium text-neutral-500 sm:h-6 sm:w-23 sm:rounded-3xl sm:px-4 sm:py-[6px] sm:text-base">
                {formatBRL(amountCard2 * feesFactor2)}
              </div>
            </div>
          </div>
          {/* Cartão e CVV */}
          <div className="space-y-4 sm:flex sm:flex-wrap sm:items-center sm:gap-4 sm:space-y-0">
            <div className="flex items-center gap-1 text-sm font-semibold text-neutral-800 sm:flex-none">
              <BiCreditCard className="h-4 w-4 text-neutral-800" />
              Cartão:
            </div>

            <div className="relative flex-1 sm:flex-1">
              <select
                style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}
                className="h-9 w-full appearance-none rounded-md bg-white py-1 pl-10 pr-10 text-xs text-gray-700 shadow-sm focus:outline-none sm:h-11 sm:py-2 sm:pl-10 sm:pr-12 sm:text-xs"
                value={selectedCardExternalId2}
                onChange={(e) => {
                  setSelectedCardExternalId2(e.target.value)
                  // Preenche CVV automaticamente se disponível
                  const cvv = temporaryCvv[e.target.value]
                  if (cvv) {
                    setCvv2(cvv)
                    form?.setValue?.('cvv2', cvv, { shouldValidate: true })
                  } else {
                    setCvv2('')
                    form?.setValue?.('cvv2', '', { shouldValidate: true })
                  }
                }}>
                {cardsForSecond.map((card: SavedCardUI) => (
                  <option key={card.id} value={card.external_id} className="py-2 text-base sm:py-1 sm:text-xs">
                    {`**** ${card.last4}`}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                {getCardIcon(cards.find((c: SavedCardUI) => c.external_id === selectedCardExternalId2)?.brand || '')}
              </div>
              <MdKeyboardArrowDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 sm:right-3 sm:h-6 sm:w-5" />
            </div>

            <div className="flex items-center gap-2 sm:flex-none">
              <span className="text-sm text-neutral-500">
                CVV<span className="text-red-500">*</span>
              </span>
              <input
                id="cvv-input-2"
                aria-label="CVV"
                style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}
                className="h-11 w-16 rounded-md bg-gray-100 px-2 py-1 text-sm text-gray-700 outline-none sm:w-18"
                placeholder="123"
                {...(form?.register ? form.register('cvv2', { required: true, pattern: /^\d{3,4}$/u }) : {})}
                maxLength={4}
                value={cvv2}
                onChange={(e) => {
                  setCvv2(e.target.value)
                  form?.setValue?.('cvv2', e.target.value, { shouldValidate: true })
                }}
              />
              <button
                onClick={() => setShowForm2(true)}
                className="flex items-center border-0 bg-transparent text-xs font-medium text-primary-800 hover:underline">
                <MdOutlineAddCard className="mr-1 h-4 w-4 text-primary-800" />
                <span className="hidden sm:inline">Adicionar</span>
                <span className="sm:hidden">+</span>
              </button>
            </div>
          </div>

          <div>
            <hr
              style={{
                borderTop: '0.3px solid #E3E3E3',
                borderLeft: 'none',
                borderRight: 'none',
                borderBottom: 'none'
              }}
            />
          </div>

          {/* Parcelas - Cartão 2 */}
          <div className="space-y-2 sm:flex sm:items-center sm:gap-7 sm:space-y-0">
            <span className="block text-sm text-gray-700 sm:whitespace-nowrap">Parcelas</span>
            <div className="relative flex-1">
              <select
                id="installments-select-2"
                aria-label="Parcelas"
                style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}
                className="h-9 w-full appearance-none rounded-md bg-white px-4 pr-8 text-sm text-gray-700 shadow-sm focus:outline-none"
                value={installments2 || ''}
                disabled={loadingInstallments}
                onChange={(e) => {
                  const { value } = e.target
                  if (value === '') {
                    setInstallments2(null)
                    return
                  }
                  setInstallments2(Number(value))
                }}>
                <option value="">Selecione a quantidade de parcelas</option>
                {installmentsList.map((it) => {
                  const fraction = totalAmount > 0 ? amountCard2 / totalAmount : 0
                  const perInstallment = Number(it.amount) * fraction
                  const perTotal = Number(it.total) * fraction
                  const label = `${it.installment} de ${formatBRL(perInstallment)} ${
                    it.fees === '(sem juros)' ? '- sem juros' : `(total ${formatBRL(perTotal)})`
                  }`
                  return (
                    <option key={it.id} value={it.id}>
                      {label}
                    </option>
                  )
                })}
              </select>
              <MdKeyboardArrowDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 sm:right-3 sm:h-6 sm:w-5" />
            </div>
          </div>

          {/* Valor a pagar - Cartão 2 */}
          <div className="flex items-center justify-between border-t border-gray-200">
            <span className="text-xs text-neutral-800 sm:text-sm">Valor a pagar Cartão 2:</span>
            <span className="text-lg font-semibold text-neutral-800 sm:text-2xl">
              {formatBRL(amountCard2 * feesFactor2)}
            </span>
          </div>
        </div>

        {showForm2 && (
          <CardAddForm
            onSubmit={handleAddCard2}
            onCancel={() => setShowForm2(false)}
            shippingAddress={shippingAddress}
          />
        )}
      </div>
      {/* Mensagens */}
      <div className="mt-4 flex flex-col gap-2">
        {error && <div className="text-sm text-red-600">{error}</div>}
        {cards.length === 0 && !loadingCards && (
          <div className="text-xs text-neutral-700">
            Você ainda não possui cartões salvos. Adicione um cartão para continuar.
          </div>
        )}
      </div>
      <div className="mt-6">
        <WarningBanners
          containsProductWithLongerDeliveryTime={false}
          containProductsMatirz={false}
          containsProductWithHubDistributor={false}
        />
      </div>
    </div>
  )
}
